import React from 'react'

export interface FamilyMemberData {
  totalNumber: number
  hasTwins?: boolean
  hasTriplets?: boolean
  fromPreviousMarriage?: number
  deceased?: number
}

export interface FamilyData {
  children?: FamilyMemberData
  stepchildren?: FamilyMemberData
  grandchildren?: FamilyMemberData
  greatGrandchildren?: FamilyMemberData
  adoptedChildren?: FamilyMemberData
  fosterChildren?: FamilyMemberData
}

export interface ValidationError {
  field: string
  message: string
}

export function validateFamilyMember (
  data: FamilyMemberData,
  memberType: string
): ValidationError[] {
  const errors: ValidationError[] = []

  if ((data.fromPreviousMarriage ?? 0) > 0 && (data.fromPreviousMarriage ?? 0) > data.totalNumber) {
    errors.push({
      field: `${memberType}.fromPreviousMarriage`,
      message: `Previous marriage count cannot exceed total ${memberType} count`
    })
  }

  if ((data.deceased ?? 0) > 0 && (data.deceased ?? 0) > data.totalNumber) {
    errors.push({
      field: `${memberType}.deceased`,
      message: `Deceased count cannot exceed total ${memberType} count`
    })
  }

  // Validate twins logic
  if (data.hasTwins === true && data.totalNumber < 2) {
    errors.push({
      field: `${memberType}.hasTwins`,
      message: `Cannot have twins with fewer than 2 ${memberType}`
    })
  }

  // Validate triplets logic
  if (data.hasTriplets === true && data.totalNumber < 3) {
    errors.push({
      field: `${memberType}.hasTriplets`,
      message: `Cannot have triplets with fewer than 3 ${memberType}`
    })
  }

  // If both twins and triplets are selected, minimum total should be 5
  if (data.hasTwins === true && data.hasTriplets === true && data.totalNumber < 5) {
    errors.push({
      field: `${memberType}.totalNumber`,
      message: `Cannot have both twins and triplets with fewer than 5 ${memberType}`
    })
  }

  return errors
}

export function buildFamilySegment (
  data: FamilyMemberData,
  singularLabel: string,
  pluralLabel: string
): string {
  if (data.totalNumber === 0 || data.totalNumber < 0) {
    return ''
  }

  let segment = ''

  // Base count with singular/plural
  const label = data.totalNumber === 1 ? singularLabel : pluralLabel
  segment += `${data.totalNumber} ${label}`

  // Build modifiers array
  const modifiers: string[] = []

  if (data.hasTwins === true) {
    modifiers.push('twins')
  }

  if (data.hasTriplets === true) {
    modifiers.push('triplets')
  }

  if ((data.fromPreviousMarriage ?? 0) > 0) {
    modifiers.push(`${data.fromPreviousMarriage ?? 0} from previous marriage`)
  }

  if ((data.deceased ?? 0) > 0) {
    modifiers.push(`${data.deceased ?? 0} deceased`)
  }

  // Add modifiers if any exist
  if (modifiers.length > 0) {
    segment += ` (${modifiers.join(', ')})`
  }

  return segment
}

export function cleanupFamilyString (familyString: string): string {
  let cleaned = familyString

  // Remove leading and trailing spaces
  cleaned = cleaned.trim()

  // Remove trailing semicolons
  while (cleaned.endsWith(';')) {
    cleaned = cleaned.slice(0, -1).trim()
  }

  return cleaned
}

export function buildFamilyString (familyData: FamilyData): {
  familyString: string
  errors: ValidationError[]
} {
  const errors: ValidationError[] = []
  const segments: string[] = []

  // Define family member types with their labels
  const familyTypes = [
    { key: 'children', data: familyData.children, singular: 'child', plural: 'children' },
    { key: 'stepchildren', data: familyData.stepchildren, singular: 'stepchild', plural: 'stepchildren' },
    { key: 'grandchildren', data: familyData.grandchildren, singular: 'grandchild', plural: 'grandchildren' },
    { key: 'greatGrandchildren', data: familyData.greatGrandchildren, singular: 'great-grandchild', plural: 'great-grandchildren' },
    { key: 'adoptedChildren', data: familyData.adoptedChildren, singular: 'adopted child', plural: 'adopted children' },
    { key: 'fosterChildren', data: familyData.fosterChildren, singular: 'foster child', plural: 'foster children' }
  ]

  // Process each family type
  for (const familyType of familyTypes) {
    if (familyType.data != null) {
      // Validate the data
      const memberErrors = validateFamilyMember(familyType.data, familyType.key)
      errors.push(...memberErrors)

      // Build segment if no validation errors for this member type
      if (memberErrors.length === 0) {
        const segment = buildFamilySegment(
          familyType.data,
          familyType.singular,
          familyType.plural
        )

        if (segment.length > 0) {
          segments.push(segment)
        }
      }
    }
  }

  // Join segments with semicolons and clean up
  let familyString = segments.join('; ')
  familyString = cleanupFamilyString(familyString)

  // Check length limit (200 characters as per original)
  if (familyString.length > 200) {
    errors.push({
      field: 'familyString',
      message: `Family string is ${familyString.length - 200} characters too long (max 200 characters)`
    })
  }

  return {
    familyString,
    errors
  }
}

export function parseFamilyString (familyString: string): FamilyData {
  if (familyString.length === 0 || familyString.trim().length === 0) {
    return {}
  }

  const familyData: FamilyData = {}

  // Split by semicolons to get individual segments
  const segments = familyString.split(';').map(s => s.trim()).filter(s => s.length > 0)

  for (const segment of segments) {
    // Extract the number and type from each segment
    const match = segment.match(/^(\d+)\s+(.+?)(?:\s*\((.+)\))?$/)
    if (match == null) continue

    const [, countStr, typeStr, modifiersStr] = match
    const count = parseInt(countStr, 10)

    // Determine the family member type
    let memberType: keyof FamilyData | null = null
    const lowerType = typeStr.toLowerCase()

    // Define type mappings with keywords that must all be present
    const typeMappings = [
      { keywords: ['great', 'grandchild'], type: 'greatGrandchildren' },
      { keywords: ['grandchild'], type: 'grandchildren' },
      { keywords: ['stepchild'], type: 'stepchildren' },
      { keywords: ['adopted', 'child'], type: 'adoptedChildren' },
      { keywords: ['foster', 'child'], type: 'fosterChildren' },
      { keywords: ['child'], type: 'children' }
    ]

    // Find the first mapping where all keywords are present
    const memberTypeString = typeMappings.find(mapping =>
      mapping.keywords.every(keyword => lowerType.includes(keyword))
    )?.type ?? null

    if (memberTypeString == null) continue

    memberType = memberTypeString as keyof FamilyData

    // Parse modifiers
    const memberData: FamilyMemberData = { totalNumber: count }

    if (modifiersStr != null) {
      const modifiers = modifiersStr.split(',').map(m => m.trim())

      // Define modifier parsers with their detection and processing logic
      const modifierParsers = [
        {
          keyword: 'twins',
          process: () => { memberData.hasTwins = true }
        },
        {
          keyword: 'triplets',
          process: () => { memberData.hasTriplets = true }
        },
        {
          keyword: 'from previous marriage',
          process: (modifier: string) => {
            const match = modifier.match(/(\d+)\s+from previous marriage/)
            if (match != null) {
              memberData.fromPreviousMarriage = parseInt(match[1], 10)
            }
          }
        },
        {
          keyword: 'deceased',
          process: (modifier: string) => {
            const match = modifier.match(/(\d+)\s+deceased/)
            if (match != null) {
              memberData.deceased = parseInt(match[1], 10)
            }
          }
        }
      ]

      // Process each modifier
      for (const modifier of modifiers) {
        const parser = modifierParsers.find(p => modifier.includes(p.keyword))
        if (parser !== null && parser !== undefined) {
          parser.process(modifier)
        }
      }
    }

    familyData[memberType] = memberData
  }

  return familyData
}

export function useFamilyString (initialData?: FamilyData): {
  familyData: FamilyData
  familyString: string
  errors: ValidationError[]
  updateFamilyMember: (memberType: keyof FamilyData, data: FamilyMemberData) => void
  clearFamilyMember: (memberType: keyof FamilyData) => void
  setFamilyData: React.Dispatch<React.SetStateAction<FamilyData>>
} {
  const [familyData, setFamilyData] = React.useState<FamilyData>(initialData ?? {})
  const [result, setResult] = React.useState<{ familyString: string, errors: ValidationError[] }>({
    familyString: '',
    errors: []
  })

  // Update family string whenever data changes
  React.useEffect(() => {
    const newResult = buildFamilyString(familyData)
    setResult(newResult)
  }, [familyData])

  const updateFamilyMember = (
    memberType: keyof FamilyData,
    data: FamilyMemberData
  ): void => {
    setFamilyData(prev => ({
      ...prev,
      [memberType]: data
    }))
  }

  const clearFamilyMember = (memberType: keyof FamilyData): void => {
    setFamilyData(prev => {
      const { [memberType]: _, ...updated } = prev
      return updated
    })
  }

  return {
    familyData,
    familyString: result.familyString,
    errors: result.errors,
    updateFamilyMember,
    clearFamilyMember,
    setFamilyData
  }
}
